/**
 * Checks if a string is in base64 format, returning boolean
 *
 * @param {string} string String to test
 */
export const isBase64 = (string) => {
  const regex =
    /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/
  return regex.test(string)
}

import { LitElement, css, html, unsafeCSS } from '@lit'

const imageConverter = (value, type) => {
  const imageData = isBase64(value) ? atob(value) : value
  try {
    return JSON.parse(imageData)
  } catch (e) {
    return imageData
  }
}
class NovaAdaptiveImage extends LitElement {
  static properties = {
    image: {
      type: String,
      attribute: 'data-image',
      converter: imageConverter,
    },
    mobileImage: {
      type: String,
      attribute: 'data-mobileImage',
      converter: imageConverter,
    },
  }

  constructor() {
    super()
    // This list of asset widths to select from will be comprehensive for most use cases
    this.srcsetWidths = [
      246, 320, 400, 480, 560, 640, 700, 768, 836, 904, 1024, 1280, 1366, 1440,
      1600, 1920, 2560,
    ]
  }

  connectedCallback() {
    super.connectedCallback()

    this.width = this.dataset.width || this.image.width || 1920
    this.mobileWidth = this.mobileImage?.width || 768
    this.src = this.image?.src || this.image
    this.mobileSrc = this.mobileImage?.src || this.mobileImage
    this.alt = this.dataset.alt || this.image?.alt || ''
    this.sizes = this.dataset.sizes || '100vw'
    this.loading = this.dataset.loading || 'lazy'
    this.objectFit = this.dataset.objectFit || 'cover'
    this.aspectRatio = Number.parseFloat(this.dataset.aspectRatio) || 1
    this.mobileAspectRatio =
      Number.parseFloat(this.dataset.mobileAspectRatio) || this.aspectRatio || 1

    this.style.setProperty('--nova-object-fit', this.objectFit)
    this.style.setProperty('--nova-aspect-ratio', this.aspectRatio)
    this.style.setProperty('--nova-mobile-aspect-ratio', this.mobileAspectRatio)
  }

  updateImage(data) {
    Object.assign(this, data)
    this.render()
  }

  updated(changedProperties) {
    if (changedProperties.has('image')) {
      this.src = this.image?.src || this.image
      this.mobileSrc = this.mobileImage?.src || this.mobileImage
      this.alt = this.dataset.alt || this.image?.alt || ''
      this.requestUpdate()
    }
  }

  generateSrcset(src, imgWidth) {
    let srcset = ''

    for (const width of this.srcsetWidths) {
      if (imgWidth >= width) srcset += `${src}&width=${width} ${width}w,`
    }
    return srcset
  }

  render() {
    return html`
      <picture>
        ${
          this.mobileImage
            ? html`<source
                media="(min-width: 750px)"
                srcset=${this.generateSrcset(this.src, this.width)}
                sizes=${this.sizes}
              />
              <source
                media="(max-width: 749px)"
                srcset=${this.generateSrcset(this.mobileSrc, this.mobileWidth)}
                sizes=${this.sizes}
              />`
            : html`<source
              srcset=${this.generateSrcset(this.src, this.width)}
              sizes=${this.sizes}
            />`
        }

        <img
          id="image-element"
          src=${this.src}&width=${this.srcsetWidths[0]}
          alt=${this.alt}
          loading=${this.loading}
        />
      </picture>
    `
  }

  static styles = css`
    :host {
      display: block;
      height: auto;
      position: relative;
      width: 100%;
      aspect-ratio: var(--nova-mobile-aspect-ratio);
    }
    @media screen and (min-width: 750px) {
      :host {
        aspect-ratio: var(--nova-aspect-ratio);
      }
    }
    @supports not (aspect-ratio: ${unsafeCSS('var(--nova-aspect-ratio)')}) {
      :host::before {
        float: left;
        padding-top: calc(100% / ${unsafeCSS('var(--nova-aspect-ratio)')});
        content: "";
      }
      :host::after {
        display: block;
        clear: both;
        content: "";
      }
    }
    #image-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--nova-object-fit);
      -webkit-transform: translate3d(0, 0, 0);
    }
  `
}

customElements.define('nova-adaptive-image', NovaAdaptiveImage)
